import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserService } from "@/services/userService";
import DashboardLayout from "@/components/layout/DashboardLayout";
import PageHeader from "@/components/common/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { User, Mail, Lock, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const roleLabels: Record<string, string> = {
  student: "Student",
  growlearn: "GrowLearn",
  aca_member: "ACA Member",
};

const CreateUser = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ full_name: "", email: "", password: "", role: "" });

  const allowedRoles = [
    { value: "student", label: "Student" },
    { value: "growlearn", label: "GrowLearn" },
    { value: "aca_member", label: "ACA Member" },
  ];

  const handleCreateUser = async () => {
    if (!newUser.full_name || !newUser.email || !newUser.password || !newUser.role) {
      toast({ title: "Missing fields", description: "Please fill in every field.", variant: "destructive" });
      return;
    }
    setCreating(true);
    try {
      await UserService.create({
        email: newUser.email,
        password: newUser.password,
        full_name: newUser.full_name,
        role: newUser.role as any,
      });
      toast({ title: "User created", description: `${newUser.full_name} created as ${roleLabels[newUser.role] || newUser.role}.` });
      navigate("/growlearn/users");
    } catch (err: any) {
      toast({ title: "Error", description: err.response?.data?.error || err.message, variant: "destructive" });
      setCreating(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Back */}
        <button
          onClick={() => navigate("/growlearn/users")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to Manage Users
        </button>

        <PageHeader title="Create User" crumbLabel="Manage Users" crumbTo="/growlearn/users" />

        <Card className="glass-premium shadow-sm max-w-2xl">
          <CardContent className="p-6 md:p-8">
            <h3 className="border-l-[3px] border-primary pl-3 text-base font-bold text-foreground">Account Details</h3>
            <p className="mt-1 pl-3 text-sm text-muted-foreground">Add a user and assign their role.</p>

            {/* Prevent browser autofill of the credentials below. */}
            <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleCreateUser(); }} className="mt-6 space-y-5">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="h-11 pl-9 bg-muted/40"
                    autoComplete="off"
                    name="new-user-name"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="h-11 pl-9 bg-muted/40"
                    type="email"
                    autoComplete="off"
                    name="new-user-email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="h-11 pl-9 bg-muted/40"
                    type="password"
                    autoComplete="new-password"
                    name="new-user-password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Min 6 characters"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Role</Label>
                <Select value={newUser.role} onValueChange={(val) => setNewUser({ ...newUser, role: val })}>
                  <SelectTrigger className="h-11 bg-muted/40"><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    {allowedRoles.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button type="submit" className="gradient-warm border-0 text-white shadow-md h-11 px-6" disabled={creating}>
                  {creating ? "Creating…" : "Create User"}
                </Button>
                <Button type="button" variant="outline" className="h-11 px-6" onClick={() => navigate("/growlearn/users")} disabled={creating}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default CreateUser;
